/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { gnoment, Gnoment } from 'shared-ui/gnoment';

export type Permit = {
    id: string;
    name: string;
    time_zone: string;
};

export type RawDailyLottery = {
    id: string;
    trip_date: string;
    lottery_closes: string;
    lottery_opens: string;
    results_available: string;
    available_quota: number;
};

export type DailyLottery = {
    id: string;
    trip_date: Gnoment;
    lottery_closes: Gnoment;
    lottery_opens: Gnoment;
    results_available: Gnoment;
    available_quota: number;
    status: string;
    actions?: {
        name?: string;
        link?: string;
    };
};

// Lottery Schedule Status Enums
export const StatusEnums = {
    Open: 'Open',
    ComingSoon: 'Coming Soon',
    Closed: 'Closed',
    Notified: 'Notified',
};
export const StatusDetails = {
    [StatusEnums.Open]: { color: 'green', order: 1 },
    [StatusEnums.ComingSoon]: { color: 'blue', order: 2 },
    [StatusEnums.Closed]: { color: 'red', order: 3 },
    [StatusEnums.Notified]: { color: 'gray', order: 4 },
};

// EXPORTING FOR TESTS
// helper used to format daily lotteries
export const getStatus = (lottery: RawDailyLottery, permitTimeZone: string) => {
    const open = gnoment(lottery.lottery_opens).tz(permitTimeZone);
    const close = gnoment(lottery.lottery_closes).tz(permitTimeZone);
    const notify = gnoment(lottery.results_available).tz(permitTimeZone);
    const now = gnoment().tz(permitTimeZone);

    if (now.isBefore(open)) return StatusEnums.ComingSoon;
    if (now.isBefore(close)) return StatusEnums.Open;
    if (now.isBefore(notify)) return StatusEnums.Closed;
    return StatusEnums.Notified;
};

// EXPORTING FOR TESTS
// helper used to format daily lotteries
export const getAction = (
    status: string,
    lotteryId: string,
    permitId: string,
    tripDate: Gnoment
) => {
    switch (status) {
        case StatusEnums.Open:
            return {
                name: 'Register',
                screenReaderText: `Register for Daily Lottery On ${tripDate.format('MMMM D, YYYY')}`,
                link: `/permits/${permitId}/lottery/${lotteryId}/registration/__${permitId}`,
            };
        default:
            return {};
    }
};

// EXPORTING FOR TESTS
// helper used to sort daily lotteries
export const sortDailyLottery = (a: DailyLottery, b: DailyLottery): number => {
    const aOrder = StatusDetails[a.status].order;
    const bOrder = StatusDetails[b.status].order;

    // first order by status order
    if (aOrder > bOrder) {
        return 1;
    }
    if (aOrder < bOrder) {
        return -1;
    }

    // then order by trip_date
    return a.trip_date.isAfter(b.trip_date) ? 1 : -1;
};

export function formatLotteries(lotteries: RawDailyLottery[], permit: Permit) {
    const permitTimeZone = get(permit, 'time_zone');
    const permitId = get(permit, 'id');

    if (isEmpty(lotteries) || !permitId) return [];

    const dailyLotteries: DailyLottery[] = lotteries
        .map((lottery: RawDailyLottery): DailyLottery => {
            const status = getStatus(lottery, permitTimeZone);
            const tripDate = gnoment(lottery.trip_date).tz(permitTimeZone);

            return {
                ...lottery,
                trip_date: tripDate,
                lottery_opens: gnoment(lottery.lottery_opens).tz(permitTimeZone),
                lottery_closes: gnoment(lottery.lottery_closes).tz(permitTimeZone),
                results_available: gnoment(lottery.results_available).tz(permitTimeZone),
                status,
                available_quota: lottery.available_quota,
                actions: getAction(status, lottery.id, permitId, tripDate),
            };
        })
        .filter((lottery: DailyLottery) => {
            // Only include statuses the user can do something about (maybe temp?)
            if (
                lottery.status === StatusEnums.ComingSoon ||
                lottery.status === StatusEnums.Open
            ) {
                return true;
            }
            return false;
        })
        .sort(sortDailyLottery);

    return dailyLotteries;
}

export function filterLotteriesBetweenDates({
    lotteries,
    startDate,
    endDate,
}: {
    lotteries: DailyLottery[];
    startDate: Gnoment;
    endDate: Gnoment;
}) {
    const filteredLotteries: DailyLottery[] = lotteries.filter(
        ({ trip_date: tripDate }: { trip_date: Gnoment }) => {
            if (startDate && endDate) {
                return (
                    tripDate.isSameOrAfter(startDate) && tripDate.isSameOrBefore(endDate)
                );
            }
            if (startDate) return tripDate.isSameOrAfter(startDate);
            if (endDate) return tripDate.isSameOrBefore(endDate);
            return true;
        }
    );

    return filteredLotteries;
}
